import Link from "next/link";
import { Zap, Shield, Eye, Lock, Building2, Server, BarChart2, FileText, Briefcase } from "lucide-react";

const benefits = [
  {
    icon: <Zap size={20} />,
    title: "Faster delivery",
    desc: "Pre-built orchestration, monitoring, and security layers mean your project doesn't start from zero — typical timelines shrink by weeks.",
  },
  {
    icon: <Shield size={20} />,
    title: "Production-hardened from day one",
    desc: "Orchestrik has been tested in live enterprise deployments. Infrastructure problems are already solved before we start on yours.",
  },
  {
    icon: <Eye size={20} />,
    title: "Built-in observability",
    desc: "Every agent action is logged. You get full visibility into what your agents are doing, when, and why — without custom instrumentation work.",
  },
  {
    icon: <Lock size={20} />,
    title: "Security and access controls included",
    desc: "Role-based access, audit trails, and data residency controls are part of the platform — not add-ons scoped after the fact.",
  },
];

const industries = [
  { icon: <Building2 size={18} />, label: "Financial Services", href: "/industry/banking" },
  { icon: <Server size={18} />, label: "Supply Chain & Logistics", href: "/industry/supply-chain" },
  { icon: <BarChart2 size={18} />, label: "Retail & D2C", href: "/industry/retail" },
  { icon: <FileText size={18} />, label: "Life Sciences & Pharma", href: "/industry/life-sciences" },
  { icon: <Briefcase size={18} />, label: "Management Consulting", href: "/services/strategy" },
];

export default function OrchestriкSection() {
  return (
    <section className="px-8 md:px-[60px] py-14 bg-[#0f1f3d]">
      <div className="max-w-5xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-blue-400 mb-4">
          Our platform
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 leading-snug">
          We build on Orchestrik — our own AI agent platform
        </h2>
        <p className="text-gray-300 text-base md:text-lg mb-10 max-w-2xl leading-relaxed">
          Most AI projects spend the first months building infrastructure before writing a single line of business logic. We don&apos;t. Orchestrik is the platform we built for ourselves and run every client engagement on — so you skip straight to the part that matters.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-7 mb-12">
          {benefits.map(b => (
            <div key={b.title} className="flex gap-4">
              <div className="flex-shrink-0 mt-0.5 text-blue-400">{b.icon}</div>
              <div>
                <h3 className="text-white font-semibold text-sm mb-1">{b.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <Link href="/orchestrik">
          <a className="inline-block bg-white text-[#0f1f3d] font-semibold text-sm px-6 py-3 rounded-md hover:bg-gray-100 transition-colors mb-12">
            Know More →
          </a>
        </Link>

        <div className="border-t border-white/10 pt-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-5">
            Deployed across industries
          </p>
          <div className="flex flex-wrap gap-3">
            {industries.map(ind => (
              <Link key={ind.label} href={ind.href}>
                <a className="inline-flex items-center gap-2 border border-white/10 rounded-full px-4 py-2 text-sm text-gray-300 hover:border-blue-400 hover:text-blue-300 transition-colors">
                  <span className="text-blue-400">{ind.icon}</span>
                  {ind.label}
                </a>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
